import React, { useState } from 'react';
import { Modal, Box, SpaceBetween, Button, FormField, Input } from '@cloudscape-design/components';
import { useFlashbar } from 'src/common/hooks/useFlashbar';

interface ExportModalProps {
  visible: boolean;
  onClose: () => void;
  onExport: (exportName: string) => Promise<void>;
  activeFilters: Record<string, string[]>;
  onNavigateToExports?: () => void;
}

const validateExportName = (name: string) => {
  const regString = /[^a-zA-Z0-9_\-.:\s]/;
  return name.length > 0 && !regString.test(name);
};

export const ExportModal: React.FC<ExportModalProps> = ({
  visible,
  onClose,
  onExport,
  activeFilters,
  onNavigateToExports,
}) => {
  const [exportName, setExportName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { addFlashbarMessage } = useFlashbar();

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await onExport(exportName);
      addFlashbarMessage({
        type: 'success',
        header: 'Export request submitted successfully!',
        content: 'Your export is being processed.',
        action: onNavigateToExports ? (
          <Button onClick={onNavigateToExports}>Go to Exports</Button>
        ) : undefined,
        dismissible: true,
        id: 'export_success',
      });
      onClose();
      setExportName('');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setExportName('');
    onClose();
  };

  return (
    <Modal
      size="medium"
      onDismiss={handleClose}
      visible={visible}
      header="Export GL Data"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleClose} variant="link">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!validateExportName(exportName)}
              variant="primary"
              loading={isLoading}
            >
              Export
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <div>
          Export GL data with current filters applied. The export will be processed and made
          available in the Exports tab.
        </div>

        {Object.keys(activeFilters).length > 0 && (
          <div>
            <strong>Applied Filters:</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              {Object.entries(activeFilters).map(([key, values]) => {
                // Check if "all" is selected (Select All option)
                const displayValue = values.includes('all') ? 'All' : values.join(', ');

                return (
                  <li key={key}>
                    <strong>{key}:</strong> {displayValue}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <FormField
          label="Export Name"
          description="Only letters, numbers, hyphens, periods, underscores, colons, and spaces are allowed."
        >
          <Input
            value={exportName}
            onChange={({ detail }) => setExportName(detail.value.trim())}
            placeholder="Enter export name"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
